import React from 'react';
import { Box, Stack } from '@mui/material';
import moment from 'moment';

// components
import NormalSignupOrderSummary from './normal-signup-order-summary/NormalSignupOrderSummary';
import { OffersModule } from './offers-module/OffersModule';
import { SignupNutritionCoaching } from './signup-personal-coach-card/SignupPersonalCoach';
import { SupplementPackageCard } from './supplement-package-card/SupplementPackageCard';
import PromoCode from 'src/components/fields/promo-code/promo-code';

// utils & types
import { EffectivePlanObjType } from '../types';
import {
  getNutritionCoachingPricing,
  getSupplementPricing,
} from '../../subscription-new/utils/plans.util';
import { projectCouponCode } from '../../constants';
import { setIsFutureCommencement } from 'src/store/signup';
import { getFullPlanObj } from 'src/lib/utils';
import { MainPlanPromo } from '../../subscription-new/components/gift-with-purchase/constants';

// styles & assets
import colors from 'src/styles/equalution/colors';

// models
import { Plan, Coupon } from 'src/pages/deprecated/payment/types';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'src/store/hooks';
import { useCampaign } from 'src/hooks/useCampaign';

interface PlanAndPromoCodeProps {
  isMobile: boolean;
  effectivePlanObj: EffectivePlanObjType | undefined;
  isNutritionCoachingAdded: boolean;
  isSupplementAdded: boolean;
  allPlansSorted: Plan[];
  handleNutritionCoachingAdd: () => void;
  handleSupplementAdd: () => void;
  onRemoveNutritionCoaching: () => void;
  onRemoveSupplement: () => void;
  handleChangePlan: () => void;
  coupon: Coupon | undefined;
  setCoupon: (coupon: Coupon | undefined) => void;
  showFreeTrial: boolean;
  isTwelveMonthSelected: boolean;
  isFitherProjectUser: boolean;
  futureCommencement?: boolean;
  isGiftActive?: boolean;
}

export const PlanAndPromoCode: React.FC<PlanAndPromoCodeProps> = ({
  isMobile,
  effectivePlanObj,
  isNutritionCoachingAdded,
  isSupplementAdded,
  allPlansSorted,
  handleNutritionCoachingAdd,
  handleSupplementAdd,
  onRemoveNutritionCoaching,
  onRemoveSupplement,
  handleChangePlan,
  coupon,
  setCoupon,
  showFreeTrial,
  isTwelveMonthSelected,
  isFitherProjectUser,
  futureCommencement,
  isGiftActive = false,
}) => {
  const dispatch = useDispatch();
  const { selectedPlanId, addonWeek, shopifyVariants, shopifyVariantsLoaded } =
    useAppSelector(state => state.signUp);
  const { is10BDayCampaign } = useCampaign();
  const supplementInStock = shopifyVariants.some(
    v => v.is_protein_flavour === 1
  );
  const showSupplementUpsell =
    shopifyVariantsLoaded && !isGiftActive && supplementInStock;

  // Get original plan amount (before discount) for proper discount calculation
  const originalPlanObj = getFullPlanObj(
    allPlansSorted,
    selectedPlanId,
    addonWeek
  );
  const originalBillingAmount = originalPlanObj
    ? Number(originalPlanObj.unitAmount).toFixed(2)
    : Number(effectivePlanObj?.unitAmount).toFixed(2);

  // skipNextCycle promos bill cycle 1 at
  // standard price, cycle 2 free, cycle 3 onwards at standard price - the
  // discount is applied server-side after the first invoice clears (see
  // InvoicePaidService.applyPendingFreeCycleCoupon). The summary reflects the
  // user's effective committed length and the date paid billing resumes.
  const isSkipCycle = coupon?.metadata?.skipNextCycle === 'true';
  const intervalCount = effectivePlanObj?.intervalCount ?? 0;
  const isSixMonth =
    effectivePlanObj?.intervalCount === 6 &&
    effectivePlanObj?.interval === 'month';
  const displayPlanName = isSkipCycle
    ? `${intervalCount} Month + ${intervalCount} Free Months Subscription`
    : undefined;
  const displayIntervalLabel = isSkipCycle
    ? `${intervalCount * 2} months`
    : undefined;
  const renewalDate = moment()
    .add(intervalCount * 2, 'month')
    .format('MMMM D, YYYY');
  const description = showFreeTrial
    ? 'You will not be billed for your subscription today. Billing starts after your 10-day trial unless cancelled.'
    : isSkipCycle
    ? `Your subscription will automatically renew on ${renewalDate} at the regular ${intervalCount}-month subscription rate unless cancelled.`
    : 'Your subscription will automatically renew until cancelled.';

  return (
    <Stack width={'365px'} gap={'16px'} justifyContent={'flex-start'}>
      {!isMobile && (
        <Box textAlign={'center'} fontSize={'20px'} fontWeight={800}>
          Order Summary
        </Box>
      )}

      {effectivePlanObj && (
        <>
          <NormalSignupOrderSummary
            effectivePlanObj={effectivePlanObj}
            isNutritionCoachingAdded={isNutritionCoachingAdded}
            isSupplementAdded={isSupplementAdded}
            nutritionCoachingPrice={Number(
              getNutritionCoachingPricing(allPlansSorted)
            )}
            supplementPrice={Number(getSupplementPricing(allPlansSorted))}
            onRemoveNutritionCoaching={onRemoveNutritionCoaching}
            onRemoveSupplement={onRemoveSupplement}
            handleChangePlan={handleChangePlan}
            description={description}
            originalBillingAmount={originalBillingAmount}
            displayPlanName={displayPlanName}
            displayIntervalLabel={displayIntervalLabel}
            showFreeTrial={showFreeTrial}
            isGiftActive={isGiftActive}
            campaignLabel={
              is10BDayCampaign && isSixMonth ? '+ 10 weeks free' : undefined
            }
          />
        </>
      )}

      {!showFreeTrial && !isTwelveMonthSelected && (
        <Box>
          <Box
            sx={{ textTransform: 'uppercase' }}
            fontSize={'12px'}
            color={colors.BLUEBERRY_HALF}>
            Promo Code
          </Box>
          {effectivePlanObj && (
            <PromoCode
              prefillCode={
                isFitherProjectUser
                  ? projectCouponCode
                  : MainPlanPromo.enabled
                  ? MainPlanPromo.code
                  : ''
              }
              isChallengeFlowWithUrlCode={isGiftActive}
              priceId={effectivePlanObj.id}
              coupon={coupon}
              setCoupon={setCoupon}
              finalPrice={parseFloat(effectivePlanObj.unitAmount.toString())}
              isFromSignUp={true}
              productId={effectivePlanObj.productId}
              futureCommencement={futureCommencement}
              setFutureCommencement={(value: boolean) =>
                dispatch(setIsFutureCommencement(value))
              }
            />
          )}
        </Box>
      )}

      <OffersModule
        title="Maximise your transformation!"
        backgroundColor={colors.OFF_WHITE}>
        <SignupNutritionCoaching
          price={Number(getNutritionCoachingPricing(allPlansSorted))}
          isNutritionCoachingAdded={isNutritionCoachingAdded}
          onCtaClick={handleNutritionCoachingAdd}
          priceSuffix="/month"
        />
        {showSupplementUpsell && (
          <SupplementPackageCard
            price={Number(getSupplementPricing(allPlansSorted))}
            isSupplementAdded={isSupplementAdded}
            onCtaClick={handleSupplementAdd}
          />
        )}
      </OffersModule>
    </Stack>
  );
};
